import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionContext";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import {
  Plus, Search, Copy, X, SlidersHorizontal, UsersRound, UserPlus,
  MoreHorizontal, Eye, Pencil, UserMinus, Loader2,
} from "lucide-react";
import { tr, STUDENT_STATUS_LABEL, ACCESS_STATUS_LABEL } from "@/lib/labels";
import { EmptyState, PageHeader, SectionCard } from "@/components/doxa/AdminSurface";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const classLabel = {
  ON_TRACK: { label: "Em dia", cls: "doxa-badge-success" },
  ATTENTION: { label: "Atenção", cls: "doxa-badge-warning" },
  CRITICAL: { label: "Crítico", cls: "doxa-badge-critical" },
};
const academicBadge = {
  ACTIVE: "doxa-badge-success", PENDING: "doxa-badge-warning", INACTIVE: "doxa-badge-neutral",
  DROPPED: "doxa-badge-neutral", COMPLETED: "doxa-badge-orange", REJECTED: "doxa-badge-critical",
};
const ACADEMIC_OPTS = ["ALL", "ACTIVE", "PENDING", "INACTIVE", "DROPPED", "COMPLETED", "REJECTED"];
const ACCESS_OPTS = ["ALL", "ACTIVE", "INACTIVE", "BLOCKED", "PENDING"];
const ACADEMIC_SELECT_OPTIONS = ACADEMIC_OPTS.map((value) => ({
  value,
  label: value === "ALL" ? "Todos os status" : tr(STUDENT_STATUS_LABEL, value),
}));
const CLASSIFICATION_SELECT_OPTIONS = [
  { value: "ALL", label: "Toda classificação" },
  { value: "ON_TRACK", label: "Em dia" },
  { value: "ATTENTION", label: "Atenção" },
  { value: "CRITICAL", label: "Crítico" },
];
const ACCESS_SELECT_OPTIONS = ACCESS_OPTS.map((value) => ({
  value,
  label: value === "ALL" ? "Todo acesso" : tr(ACCESS_STATUS_LABEL, value),
}));

export default function Students() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can("manage.students");
  const [students, setStudents] = useState([]);
  const [cells, setCells] = useState([]);
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [classGroups, setClassGroups] = useState([]);
  const [inactivateTarget, setInactivateTarget] = useState(null);
  const [inactivating, setInactivating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlClassification = searchParams.get("classification");

  const [fAcademic, setFAcademic] = useState("ACTIVE");
  const [fCell, setFCell] = useState("ALL");
  const [fClassification, setFClassification] = useState(urlClassification || "ALL");
  const [fAccess, setFAccess] = useState("ALL");

  const load = () => api.get("/students", { params: { include_inactive: true } }).then(({ data }) => setStudents(data));
  const refreshPage = async () => {
    const [studentResponse, classResponse, cellResponse] = await Promise.all([
      api.get("/students", { params: { include_inactive: true } }),
      api.get("/class-groups"),
      api.get("/cells", { params: { include_inactive: true } }),
    ]);
    setStudents(studentResponse.data);
    setClassGroups(classResponse.data);
    setCells(cellResponse.data);
  };

  useEffect(() => { void refreshPage().catch(() => {}); }, []);
  useVisibleRefresh(() => refreshPage(), { intervalMs: 0 });
  useEffect(() => { if (urlClassification) setFClassification(urlClassification); }, [urlClassification]);

  const filtered = students.filter((s) => {
    if (q && !s.full_name.toLowerCase().includes(q.toLowerCase())) return false;
    if (fAcademic !== "ALL" && s.status !== fAcademic) return false;
    if (fCell !== "ALL" && s.cell_group !== fCell) return false;
    if (fClassification !== "ALL" && s.classification !== fClassification) return false;
    if (canManage && fAccess !== "ALL" && s.access_status !== fAccess) return false;
    return true;
  });

  const openStudent = (studentId, tab) => navigate(`/students/${studentId}${tab ? `?tab=${tab}` : ""}`);

  const copyInvite = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/first-access/${token}`);
    toast.success("Link de convite copiado!");
  };

  const confirmInactivate = async () => {
    if (!inactivateTarget) return;
    setInactivating(true);
    try {
      await api.post(`/students/${inactivateTarget.id}/inactivate`);
      toast.success("Status acadêmico alterado para Inativo. Histórico preservado.");
      setInactivateTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInactivating(false);
    }
  };

  const clearFilters = () => {
    setFAcademic("ALL");
    setFCell("ALL");
    setFClassification("ALL");
    setFAccess("ALL");
    setQ("");
    setSearchParams({});
  };

  const activeStudents = students.filter((s) => s.status === "ACTIVE");
  const activeCount = activeStudents.length;
  const attentionCount = activeStudents.filter((s) => s.classification === "ATTENTION").length;
  const criticalCount = activeStudents.filter((s) => s.classification === "CRITICAL").length;
  const cellFilterOptions = [
    { value: "ALL", label: "Todas as células" },
    ...cells.map((cell) => ({ value: cell.name, label: cell.name })),
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-5 md:p-6" data-testid="students-page">
      <PageHeader
        compact
        eyebrow="Gestão acadêmica"
        title="Alunos"
        description="Acompanhe a jornada acadêmica, o acesso e os pontos de atenção da escola."
        meta={`${activeCount} ativo(s) · ${attentionCount} em atenção · ${criticalCount} crítico(s)`}
        action={canManage ? (
          <button
            type="button"
            onClick={() => setOpenCreate(true)}
            className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5"
            data-testid="new-student-btn"
          >
            <Plus className="h-4 w-4" /> Novo aluno
          </button>
        ) : null}
      />

      <SectionCard
        compact
        title="Encontrar alunos"
        description="Busque pelo nome e refine a lista sem perder o contexto da operação."
        icon={SlidersHorizontal}
        testId="students-filters"
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-11 items-center gap-2 rounded-xl border border-[#E0D9CD] bg-[#FBF9F5] px-3 transition focus-within:border-[#F75A1B] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#F75A1B]/10">
            <Search className="h-4 w-4 shrink-0 text-[#8A8378]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar aluno por nome…"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[#A59C90]"
              data-testid="students-search"
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="rounded-md p-1 text-[#8A8378] hover:bg-[#F3EFE7] hover:text-[#1F1F1F]" aria-label="Limpar busca">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <DoxaSelect
              value={fAcademic}
              onValueChange={setFAcademic}
              options={ACADEMIC_SELECT_OPTIONS}
              testId="filter-academic"
              ariaLabel="Filtrar por status acadêmico"
            />
            <DoxaSelect
              value={fCell}
              onValueChange={setFCell}
              options={cellFilterOptions}
              testId="filter-cell"
              ariaLabel="Filtrar por célula"
            />
            <DoxaSelect
              value={fClassification}
              onValueChange={setFClassification}
              options={CLASSIFICATION_SELECT_OPTIONS}
              testId="filter-classification"
              ariaLabel="Filtrar por classificação"
            />
            {canManage && (
              <DoxaSelect
                value={fAccess}
                onValueChange={setFAccess}
                options={ACCESS_SELECT_OPTIONS}
                testId="filter-access"
                ariaLabel="Filtrar por acesso"
              />
            )}
            <button
              type="button"
              onClick={clearFilters}
              className="h-10 rounded-xl px-3 text-xs font-semibold text-[#6B655E] transition hover:bg-[#FFF0E9] hover:text-[#D94A12]"
              data-testid="clear-filters"
            >
              Limpar filtros
            </button>
          </div>
        </div>
      </SectionCard>

      <section className="overflow-hidden rounded-[16px] border border-[#E0D9CD] bg-white" aria-labelledby="students-list-title">
        <header className="flex flex-col gap-1 border-b border-[#EBE5DB] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 id="students-list-title" className="font-display text-lg font-bold">Lista de alunos</h2>
            <p className="mt-0.5 text-xs text-[#6B655E]">
              {filtered.length} exibido(s){filtered.length !== students.length ? ` de ${students.length}` : ""}
            </p>
          </div>
          <div className="hidden items-center gap-2 text-[11px] text-[#8A8378] md:flex">
            <span className="inline-flex h-2 w-2 rounded-full bg-[#2F855A]" /> Em dia
            <span className="ml-2 inline-flex h-2 w-2 rounded-full bg-[#C05621]" /> Atenção
            <span className="ml-2 inline-flex h-2 w-2 rounded-full bg-[#C53030]" /> Crítico
          </div>
        </header>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F1] text-left text-[11px] uppercase tracking-[0.08em] text-[#8A8378]">
              <tr>
                <th className="px-5 py-3 font-semibold">Aluno</th>
                <th className="px-4 py-3 font-semibold">Célula</th>
                <th className="px-4 py-3 font-semibold">Acadêmico</th>
                {canManage && <th className="hidden px-4 py-3 font-semibold lg:table-cell">Acesso</th>}
                <th className="px-4 py-3 font-semibold">Acompanhamento</th>
                {canManage && <th className="px-4 py-3 font-semibold">Convite</th>}
                <th className="w-16 px-4 py-3"><span className="sr-only">Ações</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EBE5DB]">
              {filtered.map((s) => {
                const c = classLabel[s.classification] || classLabel.ON_TRACK;
                return (
                  <tr
                    key={s.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => openStudent(s.id)}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && e.currentTarget === e.target) {
                        e.preventDefault();
                        openStudent(s.id);
                      }
                    }}
                    className="group cursor-pointer transition hover:bg-[#FFF9F5] focus-visible:bg-[#FFF9F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#F75A1B]/30"
                    data-testid={`student-row-${s.id}`}
                    aria-label={`Abrir ${s.full_name}`}
                  >
                    <td className="px-5 py-3.5">
                      <span className="font-semibold text-[#1F1F1F] transition group-hover:text-[#D94A12]">{s.full_name}</span>
                    </td>
                    <td className="px-4 py-3.5 text-[#6B655E]">{s.cell_group || "—"}</td>
                    <td className="px-4 py-3.5"><span className={`doxa-badge ${academicBadge[s.status] || "doxa-badge-neutral"}`}>{tr(STUDENT_STATUS_LABEL, s.status)}</span></td>
                    {canManage && <td className="hidden px-4 py-3.5 text-[#6B655E] lg:table-cell">{tr(ACCESS_STATUS_LABEL, s.access_status)}</td>}
                    <td className="px-4 py-3.5"><span className={`doxa-badge ${c.cls}`}>{c.label}</span></td>
                    {canManage && (
                      <td className="px-4 py-3.5">
                        {s.invite_token ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); copyInvite(s.invite_token); }}
                            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-[#F75A1B] transition hover:bg-[#FFF0E9] hover:text-[#D94A12]"
                            data-testid={`copy-invite-${s.id}`}
                          >
                            <Copy className="h-3.5 w-3.5" /> Copiar
                          </button>
                        ) : <span className="text-xs text-[#A59C90]">—</span>}
                      </td>
                    )}
                    <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <StudentActionsMenu
                        student={s}
                        canManage={canManage}
                        onOpen={() => openStudent(s.id)}
                        onEdit={() => openStudent(s.id, "data")}
                        onCopyInvite={() => copyInvite(s.invite_token)}
                        onInactivate={() => setInactivateTarget(s)}
                      />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6">
                    <div className="text-center">
                      <EmptyState
                        icon={UsersRound}
                        title="Nenhum aluno encontrado"
                        description="Ajuste a busca ou os filtros para voltar a encontrar alunos."
                      />
                      {canManage && (
                        <button onClick={() => setOpenCreate(true)} className="doxa-btn-primary -mt-3 inline-flex items-center gap-2 px-4 py-2" data-testid="empty-new-student">
                          <Plus className="h-4 w-4" /> Cadastrar aluno
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-2 p-3 md:hidden" data-testid="students-cards-mobile">
          {filtered.map((s) => {
            const c = classLabel[s.classification] || classLabel.ON_TRACK;
            return (
              <article key={s.id} className="rounded-[14px] border border-[#EBE5DB] bg-[#FFFEFC] p-4" data-testid={`student-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <button type="button" onClick={() => openStudent(s.id)} className="min-w-0 text-left">
                    <span className="font-display text-base font-bold leading-tight text-[#1F1F1F]">{s.full_name}</span>
                    <span className="mt-1 block text-xs text-[#6B655E]">{s.cell_group ? `Célula ${s.cell_group}` : "Sem célula vinculada"}</span>
                  </button>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span className={`doxa-badge ${c.cls}`}>{c.label}</span>
                    <StudentActionsMenu
                      student={s}
                      canManage={canManage}
                      onOpen={() => openStudent(s.id)}
                      onEdit={() => openStudent(s.id, "data")}
                      onCopyInvite={() => copyInvite(s.invite_token)}
                      onInactivate={() => setInactivateTarget(s)}
                    />
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`doxa-badge ${academicBadge[s.status] || "doxa-badge-neutral"}`}>{tr(STUDENT_STATUS_LABEL, s.status)}</span>
                  {canManage && <span className="doxa-badge doxa-badge-neutral">Acesso: {tr(ACCESS_STATUS_LABEL, s.access_status)}</span>}
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-[#EBE5DB] pt-3">
                  {canManage && s.invite_token ? (
                    <button
                      type="button"
                      onClick={() => copyInvite(s.invite_token)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F75A1B]"
                      data-testid={`copy-invite-mobile-${s.id}`}
                    >
                      <Copy className="h-3.5 w-3.5" /> Copiar convite
                    </button>
                  ) : <span />}
                  <button type="button" onClick={() => openStudent(s.id)} className="text-xs font-semibold text-[#F75A1B]" data-testid={`view-student-mobile-${s.id}`}>
                    Abrir detalhes
                  </button>
                </div>
              </article>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center">
              <EmptyState
                icon={UsersRound}
                title="Nenhum aluno encontrado"
                description="Ajuste a busca ou os filtros para voltar a encontrar alunos."
              />
              {canManage && (
                <button onClick={() => setOpenCreate(true)} className="doxa-btn-primary -mt-3 inline-flex items-center gap-2 px-4 py-2" data-testid="empty-new-student-mobile">
                  <Plus className="h-4 w-4" /> Cadastrar aluno
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {openCreate && (
        <NewStudentModal classGroups={classGroups} onClose={() => setOpenCreate(false)} onCreated={load} />
      )}

      <AlertDialog open={!!inactivateTarget} onOpenChange={(open) => { if (!inactivating && !open) setInactivateTarget(null); }}>
        <AlertDialogContent data-testid="inactivate-student-list-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar status acadêmico?</AlertDialogTitle>
            <AlertDialogDescription>
              {inactivateTarget?.full_name} deixará de aparecer como aluno academicamente ativo. Presenças, notas, tarefas, justificativas e demais históricos serão preservados. O acesso da conta não será alterado automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={inactivating}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void confirmInactivate(); }} disabled={inactivating} className="bg-[#C53030] hover:bg-[#A52626]">
              {inactivating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />} Inativar aluno
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StudentActionsMenu({ student, canManage, onOpen, onEdit, onCopyInvite, onInactivate }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="grid h-8 w-8 place-items-center rounded-lg text-[#8A8378] transition hover:bg-[#FFF0E9] hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 data-[state=open]:bg-[#FFF0E9] data-[state=open]:text-[#F75A1B]"
          aria-label={`Ações de ${student.full_name}`}
          data-testid={`student-actions-${student.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={7}
        className="w-56 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]"
        data-testid={`student-actions-menu-${student.id}`}
      >
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações do aluno</span>
          <span className="mt-0.5 block truncate text-xs font-semibold normal-case tracking-normal text-[#4A4640]">{student.full_name}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        <DropdownMenuItem onSelect={onOpen} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]">
          <Eye className="h-4 w-4" /> Abrir detalhes
        </DropdownMenuItem>
        {canManage && (
          <DropdownMenuItem onSelect={onEdit} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]">
            <Pencil className="h-4 w-4" /> Editar dados
          </DropdownMenuItem>
        )}
        {canManage && student.invite_token && (
          <DropdownMenuItem onSelect={onCopyInvite} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]">
            <Copy className="h-4 w-4" /> Copiar convite
          </DropdownMenuItem>
        )}
        {canManage && student.status === "ACTIVE" && (
          <>
            <DropdownMenuSeparator className="bg-[#EBE5DB]" />
            <DropdownMenuItem onSelect={onInactivate} className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#B83232] focus:bg-[#FFF1F1] focus:text-[#A52626]">
              <UserMinus className="h-4 w-4" /> Inativar aluno
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NewStudentModal({ classGroups, onClose, onCreated }) {
  const [cells, setCells] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cell_id: "", birth_date: "", class_group_id: classGroups[0]?.id || "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cells").then(({ data }) => {
      setCells(data);
      if (data[0] && !form.cell_id) setForm((f) => ({ ...f, cell_id: data[0].id }));
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (!form.cell_id) { toast.error("Selecione uma célula."); setBusy(false); return; }
    try {
      const { data } = await api.post("/students", form);
      toast.success("Aluno criado!");
      const url = `${window.location.origin}/first-access/${data.invite_token}`;
      navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Link de convite copiado para envio via WhatsApp.");
      onCreated();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const fieldCls = "h-11 w-full rounded-xl border border-[#E0D9CD] bg-white px-3 text-sm outline-none transition placeholder:text-[#A59C90] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15";
  const cellOptions = cells.map((cell) => ({ value: cell.id, label: cell.name }));
  const classOptions = classGroups.map((classGroup) => ({ value: classGroup.id, label: classGroup.name }));

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#1F1712]/45 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div className="w-full max-w-xl overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_24px_80px_rgba(45,31,20,.18)]" onClick={(e) => e.stopPropagation()} data-testid="new-student-modal">
        <div className="flex items-start justify-between gap-4 border-b border-[#EBE5DB] px-5 py-5 sm:px-6">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><UserPlus className="h-5 w-5" /></span>
            <div>
              <h2 className="font-display text-xl font-bold">Novo aluno</h2>
              <p className="mt-1 text-xs leading-relaxed text-[#6B655E]">Cadastre os dados básicos e gere o convite de primeiro acesso.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-[#8A8378] transition hover:bg-[#F6F3EE] hover:text-[#1F1F1F]" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit}>
          <div className="grid gap-4 px-5 py-5 sm:grid-cols-2 sm:px-6">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Nome completo</label>
              <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={fieldCls} data-testid="new-student-full_name" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">E-mail</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fieldCls} data-testid="new-student-email" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Telefone / WhatsApp</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fieldCls} data-testid="new-student-phone" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Data de nascimento</label>
              <input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className={fieldCls} data-testid="new-student-birth_date" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Célula</label>
              {cells.length === 0 ? (
                <p className="rounded-xl border border-[#F5C6C6] bg-[#FFF4F4] p-3 text-xs text-[#A52626]">Nenhuma célula ativa. Cadastre uma célula primeiro em “Células”.</p>
              ) : (
                <DoxaSelect
                  value={form.cell_id}
                  onValueChange={(cell_id) => setForm({ ...form, cell_id })}
                  options={cellOptions}
                  placeholder="Selecione a célula"
                  className="h-11 w-full"
                  testId="new-student-cell_group"
                  ariaLabel="Célula"
                />
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Turma</label>
              <DoxaSelect
                value={form.class_group_id}
                onValueChange={(class_group_id) => setForm({ ...form, class_group_id })}
                options={classOptions}
                placeholder={classOptions.length ? "Selecione a turma" : "Nenhuma turma disponível"}
                disabled={!classOptions.length}
                className="h-11 w-full"
                testId="new-student-class"
                ariaLabel="Turma"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[#EBE5DB] bg-[#FAF7F1] px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <button type="button" onClick={onClose} className="h-10 rounded-xl border border-[#E0D9CD] bg-white px-4 text-sm font-semibold text-[#4A4640] transition hover:bg-[#F6F3EE]">Cancelar</button>
            <button type="submit" disabled={busy || cells.length === 0} className="doxa-btn-primary inline-flex h-10 items-center justify-center gap-2 px-4 disabled:cursor-not-allowed disabled:opacity-50" data-testid="new-student-submit">
              <Plus className="h-4 w-4" /> {busy ? "Criando…" : "Criar aluno e gerar convite"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
